import {
  ThemeColors,
  TinyDropdownMenu,
  Toolbar,
} from '@octopus-do/octopus-storybook'
import {
  ArrowPointer,
  ArrowPointerLight,
  ArrowPointerSelector,
  ArrowPointerSelectorLight,
  ShiftHotkey,
  VHotkey,
} from '@octopus-do/octopus-storybook/icons'
import type { ArtBoardCursorMode } from '@store/reducers/artboard/types'
import type React from 'react'
import { forwardRef } from 'react'

import { MENU_OFFSET } from '../../constants'

// Key the menu reports to Toolbar.Root; the picked ArtBoardCursorMode follows it.
export const CURSOR_MENU_ID = 'cursor'

const GRAB: ArtBoardCursorMode = 'grab'
const SELECT: ArtBoardCursorMode = 'select'

const cursorIcon = (mode: ArtBoardCursorMode) =>
  mode === GRAB ? ArrowPointerLight : ArrowPointerSelectorLight

// The control names the cursor that is on, whatever its click does next.
const cursorTooltip = (mode: ArtBoardCursorMode) =>
  mode === GRAB ? 'Default cursor' : 'Select pages cursor'

interface ContentProps {
  // Selecting pages edits the project, so viewers only get the default cursor.
  selectDisabled: boolean
}

const Content: React.FC<ContentProps> = ({ selectDisabled }) => (
  <TinyDropdownMenu.Group>
    <TinyDropdownMenu.Button
      hotkeys={[VHotkey]}
      icon={ArrowPointer}
      id={GRAB}
      label="Default"
    />
    <TinyDropdownMenu.Button
      disabled={selectDisabled}
      hotkeys={[ShiftHotkey, VHotkey]}
      icon={ArrowPointerSelector}
      id={SELECT}
      label="Select pages"
    />
  </TinyDropdownMenu.Group>
)

interface TriggerProps {
  mode: ArtBoardCursorMode
  // In the estimate, image and SEO modes the blue control leaves the mode
  // instead, and the chevron folds away with its menu.
  modeLocked: boolean
  onDefault: () => void
  onExit: () => void
}

// The blue control returns the default cursor; the chevron anchors and opens
// the menu (figma 86165:420860).
const Trigger = forwardRef<HTMLButtonElement, TriggerProps>(
  ({ mode, modeLocked, onDefault, onExit }, ref) => {
    const { active, toggle } = TinyDropdownMenu.useTrigger()

    return (
      <>
        <Toolbar.DropdownButton
          active={false}
          icon={modeLocked ? ArrowPointerLight : cursorIcon(mode)}
          onClick={modeLocked ? onExit : onDefault}
          selected
          tooltip={modeLocked ? 'Back to editing' : cursorTooltip(mode)}
        />
        <Toolbar.Collapse shown={!modeLocked}>
          <Toolbar.ChevronButton
            active={active}
            onClick={toggle}
            ref={ref}
            tooltip="Cursor"
          />
        </Toolbar.Collapse>
      </>
    )
  }
)

interface Props {
  mode: ArtBoardCursorMode
  modeLocked: boolean
  onDefault: () => void
  onExit: () => void
  selectDisabled: boolean
}

// The action bar is an app control, light whatever the artboard theme is.
export const CursorModeMenu: React.FC<Props> = ({
  mode,
  modeLocked,
  onDefault,
  onExit,
  selectDisabled,
}) => {
  const { onClick } = Toolbar.useToolbarClick(CURSOR_MENU_ID)

  return (
    <TinyDropdownMenu.Root
      active={[mode]}
      content={Content}
      onClick={onClick}
      selectDisabled={selectDisabled}
      sideOffset={MENU_OFFSET}
      theme={ThemeColors.light}
    >
      <Trigger
        mode={mode}
        modeLocked={modeLocked}
        onDefault={onDefault}
        onExit={onExit}
      />
    </TinyDropdownMenu.Root>
  )
}
